/**
 * @file DesktopKeepConfirmBody.tsx
 * @description What a keep or stop-keeping confirm says, and the only place the
 * artifact's whole `attachments` map is read. Consumer: `DesktopKeepConfirm`,
 * which mounts it behind `destroyOnHidden` so a closed dialog holds no such
 * subscription — `useCurrentArtifact().data` subscribes to the entire artifact
 * document, and a toolbar button that read it would re-render on every keystroke
 * in the form (SYD-2252).
 *
 * The size shown is the current version of each attachment, never the history:
 * the agent materializes one file per attachment, so the history is not what
 * lands on the disk. No transfer figure appears anywhere here — no per-pin
 * transfer state exists, and a percentage would be invented.
 *
 * The stop copy is `desktopStopKeepingSentence`, shared with Settings › Files on
 * my computer so one operation is never described two ways. It promises no
 * deletion because the agent performs none: an unpinned artifact runs
 * `runUnpin`, which leaves the visible folder untouched and marks its entries
 * `unpinned`. `FilesOnMyComputerTab`'s footer says the same thing, and the two
 * must keep saying it.
 */

import { FC } from 'react';
import { Typography } from 'antd';

import type { ArtifactAttachments } from '#types';
import { useCurrentArtifact } from '@/use/useCurrentArtifact';
import FlexCol from '../atoms/FlexCol';
import { formatBytes } from '../rendering/attachments/formatBytes';
import { desktopStopKeepingSentence } from './desktopStopKeepingSentence';

const { Text } = Typography;

export type DesktopKeepConfirmBodyProps = {
    mode: 'keep' | 'stop';
    artifactLabel: 'form' | 'record';
};

const currentVersionBytes = (attachment: ArtifactAttachments[string]): number => {
    const versionId = attachment.currentVersionId;
    const version = versionId ? attachment.versions?.[versionId] : undefined;
    return version?.sizeBytes ?? 0;
};

const DesktopKeepConfirmBody: FC<DesktopKeepConfirmBodyProps> = ({ mode, artifactLabel }) => {
    const artifact = useCurrentArtifact();
    const attachments = artifact.kind === 'none' ? undefined : artifact.data?.attachments;
    const files = Object.values((attachments ?? {}) as ArtifactAttachments);
    const totalBytes = files.reduce((total, file) => total + currentVersionBytes(file), 0);

    const holding = files.length === 1
        ? `1 file · ${formatBytes(totalBytes)}`
        : `${files.length} files · ${formatBytes(totalBytes)}`;

    return (
        <FlexCol gap={10} noGrow>
            <Text>
                {mode === 'keep'
                    ? `Syncretic File Sync copies the files in this ${artifactLabel} into your Syncretic folder and keeps them up to date. Editing one of those copies on your computer saves a new version here.`
                    : desktopStopKeepingSentence(artifactLabel)}
            </Text>
            <Text type="secondary">
                {files.length === 0
                    ? `This ${artifactLabel} holds no files yet.`
                    : holding}
            </Text>
        </FlexCol>
    );
};

export default DesktopKeepConfirmBody;
